/**
 * Custom hook for fetching Scaling Up trend data
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type { ScalingUpTrends } from '@/types/scalingUp';

interface UseScalingUpTrendsResult {
  data: ScalingUpTrends | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useScalingUpTrends(): UseScalingUpTrendsResult {
  const [data, setData] = useState<ScalingUpTrends | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = refresh
        ? '/scaling-up/trends?refresh=true'
        : '/scaling-up/trends';
      const response = await apiClient.get<{ data: ScalingUpTrends }>(url);
      setData(response.data.data);
    } catch (err) {
      try {
        handleApiError(err, 'Failed to load trend data');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load trends'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, loading, error, refresh };
}
